// import { useEffect, useState } from "react";
// import FloorMapTable from "./FloorMapTable";
// import FloorMapForm from "./FloorMapForm";
// import Modal from "components/common/Modal";
// import Alert from "components/common/Alert";
// import { useFloorMap } from "./hooks/useFloorMap";

// export default function FloorMapContainer() {
//   const {
//     data,
//     loading,
//     loadFloorMaps,
//     addFloorMap,
//     editFloorMap,
//     removeFloorMap,
//     togglePublish,
//   } = useFloorMap();

//   const [openAdd, setOpenAdd] = useState(false);
//   const [editing, setEditing] = useState(null);
//   const [alert, setAlert] = useState({ type: "", message: "" });

//   const companyId = localStorage.getItem("company_id") || "";
//   const userId = localStorage.getItem("user_id") || null;

//   useEffect(() => {
//     if (companyId) loadFloorMaps(companyId);
//   }, [companyId]);

//   useEffect(() => {
//     const handler = (e) => {
//       loadFloorMaps(e.detail);
//     };
//     window.addEventListener("companyChanged", handler);
//     return () => window.removeEventListener("companyChanged", handler);
//   }, []);

//   const showAlert = (type, message) => setAlert({ type, message });

//   const handleCreate = async (payload) => {
//     try {
//       await addFloorMap({ ...payload, company_id: companyId, created_by: userId, updated_by: userId });
//       showAlert("success", "Floor map created successfully");
//       setOpenAdd(false);
//       loadFloorMaps(companyId);
//     } catch (err) {
//       showAlert("error", err.response?.data?.message || "Failed to create floor map");
//     }
//   };

//   const handleUpdate = async (payload) => {
//     try {
//       await editFloorMap(editing._id, { ...payload, updated_by: userId });
//       showAlert("success", "Floor map updated successfully");
//       setEditing(null);
//       loadFloorMaps(companyId);
//     } catch (err) {
//       showAlert("error", err.response?.data?.message || "Failed to update floor map");
//     }
//   };

//   const handleDelete = async (row) => {
//     if (!window.confirm(`Delete "${row.map_name}"?`)) return;
//     try {
//       await removeFloorMap(row._id, { deleted_by: userId });
//       showAlert("success", "Floor map deleted");
//       loadFloorMaps(companyId);
//     } catch (err) {
//       showAlert("error", err.response?.data?.message || "Failed to delete floor map");
//     }
//   };

//   const handleTogglePublish = async (row) => {
//     const newStatus = row.status === "published" ? "draft" : "published";
//     try {
//       await togglePublish(row._id, { status: newStatus, updated_by: userId });
//       showAlert("success", `Floor map ${newStatus === "published" ? "published" : "unpublished"}`);
//       loadFloorMaps(companyId);
//     } catch (err) {
//       showAlert("error", err.response?.data?.message || "Failed to update status");
//     }
//   };

//   return (
//     <>
//       {alert.message && (
//         <Alert
//           type={alert.type}
//           message={alert.message}
//           onClose={() => setAlert({ type: "", message: "" })}
//           autoClose
//         />
//       )}

//       <div className="mb-4 flex justify-end">
//         <button
//           className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
//           onClick={() => setOpenAdd(true)}
//         >
//           + Create Floor Map
//         </button>
//       </div>

//       <FloorMapTable
//         data={data}
//         loading={loading}
//         onEdit={setEditing}
//         onDelete={handleDelete}
//         onTogglePublish={handleTogglePublish}
//       />

//       <Modal open={openAdd} onClose={() => setOpenAdd(false)}>
//         <FloorMapForm onClose={() => setOpenAdd(false)} onSubmit={handleCreate} />
//       </Modal>

//       <Modal open={!!editing} onClose={() => setEditing(null)}>
//         <FloorMapForm
//           initialData={editing}
//           onClose={() => setEditing(null)}
//           onSubmit={handleUpdate}
//         />
//       </Modal>
//     </>
//   );
// }
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  MdSearch,
  MdDownload,
  MdAddBox,
  MdPublish,
  MdUnpublished,
} from "react-icons/md";
import TablePageLayout from "components/TablePageLayout";
import Pagination from "components/Pagination";
import CustomButton from "components/CustomButton";
import Modal from "components/common/Modal";
import Alert from "components/common/Alert";
import ConfirmModal from "components/common/ConfirmModal";
import { InputField } from "components/FormFields";

import { useFloorMap } from "./hooks/useFloorMap";
import FloorMapTable from "./FloorMapTable";
import FloorMapForm from "./FloorMapForm";

export default function FloorMapContainer() {
  const {
    data,
    loading,
    paginationInfo,
    loadFloorMaps,
    addFloorMap,
    editFloorMap,
    removeFloorMap,
    togglePublish,
  } = useFloorMap();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);

  const [openAdd, setOpenAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [selectedPublishRow, setSelectedPublishRow] = useState(null);
  const [publishLoading, setPublishLoading] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState(
    localStorage.getItem("company_id") || ""
  );
  const userId = localStorage.getItem("user_id") || null;

  // 1. Debounce Search Input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // 2. Fetch Floor Maps with Dynamic Server Pagination
  const fetchMaps = useCallback(() => {
    if (selectedCompany) {
      loadFloorMaps(selectedCompany, {
        page,
        limit,
        search: debouncedSearch,
      });
    }
  }, [selectedCompany, page, limit, debouncedSearch, loadFloorMaps]);

  useEffect(() => {
    fetchMaps();
  }, [fetchMaps]);

  // 3. Listen to Navbar Organizer Changing Event
  useEffect(() => {
    const handler = (e) => {
      setSelectedCompany(e.detail);
      setPage(1);
    };
    window.addEventListener("companyChanged", handler);
    return () => window.removeEventListener("companyChanged", handler);
  }, []);

  const showAlert = (type, message) => setAlert({ type, message });

  // Row Selection Handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(data.map((item, idx) => item._id || idx));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Actions
  const handleCreate = async (payload) => {
    try {
      await addFloorMap({
        ...payload,
        company_id: selectedCompany,
        created_by: userId,
        updated_by: userId,
      });
      showAlert("success", "Floor map created successfully");
      setOpenAdd(false);
      fetchMaps();
    } catch (err) {
      showAlert(
        "error",
        err.response?.data?.message || "Failed to create floor map"
      );
    }
  };

  const handleUpdate = async (payload) => {
    try {
      await editFloorMap(editing._id, { ...payload, updated_by: userId });
      showAlert("success", "Floor map updated successfully");
      setEditing(null);
      fetchMaps();
    } catch (err) {
      showAlert(
        "error",
        err.response?.data?.message || "Failed to update floor map"
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?._id) return;
    try {
      setDeleteLoading(true);
      await removeFloorMap(deleteTarget._id, { deleted_by: userId });
      showAlert("success", "Floor map deleted successfully");
      setDeleteTarget(null);
      fetchMaps();
    } catch (err) {
      showAlert(
        "error",
        err.response?.data?.message || "Failed to delete floor map"
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleTogglePublish = async (row) => {
    setSelectedPublishRow(row);
    // const newStatus = row.status === "published" ? "draft" : "published";
    // try {
    //   await togglePublish(row._id, { status: newStatus, updated_by: userId });
    //   showAlert(
    //     "success",
    //     `Floor map ${newStatus === "published" ? "published" : "unpublished"}`
    //   );
    //   fetchMaps();
    // } catch (err) {
    //   showAlert(
    //     "error",
    //     err.response?.data?.message || "Failed to update status"
    //   );
    // }
  };

  const confirmPublish = async () => {
    try {
      const newStatus =
        selectedPublishRow?.status === "published" ? "draft" : "published";
      setPublishLoading(true);
      await togglePublish(selectedPublishRow?._id, {
        status: newStatus,
        updated_by: userId,
      });
      showAlert(
        "success",
        `Floor map ${newStatus === "published" ? "published" : "unpublished"}`
      );
      fetchMaps();
    } catch (err) {
      showAlert(
        "error",
        err.response?.data?.message || "Failed to update status"
      );
    } finally {
      setPublishLoading(false);
    }
  };

  // Dynamic CSV Export
  const handleExportCSV = () => {
    const exportData =
      selectedRows.length > 0
        ? data.filter((item, idx) => selectedRows.includes(item._id || idx))
        : data;

    if (!exportData.length) return;

    const headers = ["Map Name", "Event", "Floors Count", "Status"];
    const safeStr = (val) => String(val || "").replace(/"/g, '""');

    const rows = exportData.map((item) => [
      `"${safeStr(item.map_name || item.title || item.name)}"`,
      `"${safeStr(item.event_id?.event_name)}"`,
      `"${item.floors?.length || 0}"`,
      `"${item.status || "draft"}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `floor_maps_${
        selectedRows.length > 0 ? "selected" : "all"
      }_${Date.now()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <TablePageLayout>
      {alert.message && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert({ type: "", message: "" })}
          autoClose
        />
      )}

      {/* --- RESPONSIVE CONTROL BAR --- */}
      <div className="grid w-full grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col">
          <InputField
            label="SEARCH"
            name="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search floor maps..."
            icon={<MdSearch className="text-lg text-gray-400" />}
          />
        </div>

        <div className="hidden lg:block"></div>
        <div className="hidden lg:block"></div>

        <div className="flex items-center justify-start gap-2 sm:justify-end lg:justify-end">
          {/* <CustomButton
            label={
              selectedRows.length > 0
                ? `Export Selected (${selectedRows.length})`
                : "Export CSV"
            }
            icon={MdDownload}
            iconPos="left"
            variant="outline"
            color="#1A1A1A"
            borderColor="#02a9e0"
            disabled={data.length === 0}
            onClick={handleExportCSV}
          /> */}

          <CustomButton
            label="Create Floor Map"
            icon={MdAddBox}
            iconPos="right"
            variant="solid"
            color="#02a9e0"
            onClick={() => setOpenAdd(true)}
          />
        </div>
      </div>

      {/* --- MAIN TABLE & PAGINATION --- */}
      {loading ? (
        <div className="mt-4 rounded-xl border bg-white p-12 text-center text-sm text-gray-500 shadow-sm">
          Loading floor maps...
        </div>
      ) : (
        <div className="table-card overflow-hidden">
          <FloorMapTable
            data={data}
            selectedRows={selectedRows}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            onEdit={setEditing}
            onDelete={setDeleteTarget}
            onTogglePublish={handleTogglePublish}
          />

          <Pagination
            currentPage={page}
            totalPages={paginationInfo.totalPages}
            totalEntries={paginationInfo.totalFloorMaps}
            limit={limit}
            onPageChange={(newPage) => setPage(newPage)}
            onLimitChange={(newLimit) => setLimit(newLimit)}
          />
        </div>
      )}

      {/* Create Modal */}
      <Modal
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        title="Create Floor Map"
      >
        <FloorMapForm
          onClose={() => setOpenAdd(false)}
          onSubmit={handleCreate}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Floor Map"
      >
        <FloorMapForm
          initialData={editing}
          onClose={() => setEditing(null)}
          onSubmit={handleUpdate}
        />
      </Modal>

      {/* Confirm Delete Modal */}

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Are you sure?"
        message={`Are you sure you want to delete "${
          deleteTarget?.map_name || deleteTarget?.title || "this floor map"
        }"? This action cannot be undone.`}
        confirmText="Yes, Delete"
        loading={deleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
      <ConfirmModal
        open={Boolean(selectedPublishRow)}
        icon={
          selectedPublishRow?.status === "published" ? MdUnpublished : MdPublish
        }
        iconBgClass={
          selectedPublishRow?.status === "published"
            ? "bg-amber-50 text-amber-500"
            : "bg-green-50 text-green-600"
        }
        confirmBtnBg={
          selectedPublishRow?.status === "published" ? "#F59E0B" : "#16A34A"
        }
        title={
          selectedPublishRow?.status === "published"
            ? "Unpublish Floor Map?"
            : "Publish Floor Map?"
        }
        message={
          selectedPublishRow?.status === "published"
            ? `Are you sure you want to unpublish "${
                selectedPublishRow?.map_name || "this floor map"
              }"? It will revert to draft status.`
            : `Are you sure you want to publish "${
                selectedPublishRow?.map_name || "this floor map"
              }"? It will become visible to all active users.`
        }
        confirmText={
          selectedPublishRow?.status === "published"
            ? "Yes, Unpublish"
            : "Yes, Publish"
        }
        loading={publishLoading}
        onConfirm={confirmPublish}
        onCancel={() => setSelectedPublishRow(null)}
      />
    </TablePageLayout>
  );
}
